import React, { useCallback, useMemo, useState } from 'react';
import { Gantt } from 'src/lib/gantt-task-react/src/components/gantt/gantt';
import { ViewMode } from 'src/lib/gantt-task-react/src/types/public-types';
import dayjs from 'dayjs';
import { Switch, Skeleton, Alert, Empty } from 'antd';
import styles from './schedule.module.css';
import { columnList, CustomTask, NullToolTip } from './columns_list';
import { WorkListReponse } from '../../../model/work_response';
import { ScheduleColor } from '../../../constant/index';
import { Messages } from '../../../constant/messages';
import { DateFormat, formatDate } from '../../../utils/date_utils';
import { useWorkDetails, WorkApi } from '../task_detail/use_work_details';

/**
 * スケジュール一覧コンポーネントの表示ステータス
 */
export enum ScheduleStatus {
  none,
  show, // 表示
  loading, // ローディング中,
  error_no_data, // 該当データなし
  error_other, // その他エラー
  error_timeout,
}

/**
 * スケジュール一覧コンポーネントのプロパティ
 */
interface WorkListProps {
  // スケジュール情報
  scheduleList: WorkListReponse;
  // エラーステータス
  status: ScheduleStatus;
  // スケジュールの表示日付
  viewDate: dayjs.Dayjs;
  // 「選択したデータ有無」欄を表示するか否か
  isConflictListMark?: boolean;
}

/**
 * スケジュール一覧
 * @param scheduleList
 * @param status
 * @param viewDate
 * @param isConflictListMark
 * @constructor
 */
export const ScheduleList: React.FC<WorkListProps> = ({ scheduleList, status, viewDate, isConflictListMark = false }) => {
  const [detailApi, detailContext] = useWorkDetails();
  // 表示用に整形
  const showTasks: CustomTask[] = useMemo(() => {
    if (scheduleList === undefined || scheduleList.length <= 0) {
      return [];
    }
    return scheduleList.map((schdule, index) => {
      return {
        isSelected: (index === 0),
        id: `taskId-${schdule.id}-${schdule.planId}`,
        workId: schdule.id,
        planId: schdule.planId,
        division: schdule.division,
        floor: schdule.floor,
        block: schdule.block,
        target: schdule.target,
        content: schdule.content,
        businessDataType: schdule.businessDataType,
        sections: schdule.sections,
        start: dayjs(schdule.plan.start).toDate(),
        end: dayjs(schdule.plan.end).toDate(),
        styles: {
          backgroundColor: ScheduleColor.PLAN,
          backgroundSelectedColor: ScheduleColor.PLAN_SELECTED,
        },
      } as CustomTask;
    });
  }, [scheduleList]);

  // 表示切替
  const [view, setView] = useState<ViewMode>(ViewMode.Hour);
  const [isChecked, setIsChecked] = useState(true); // 項目表示
  const [isWorkChecked, setIsWorkChecked] = useState(true); // 作業内容

  const getColumnWidth = (v: ViewMode): number => {
    const result: number = (() => {
      switch (v) {
        case ViewMode.Hour:
          return 40;
        case ViewMode.Day:
          return 50;
        case ViewMode.Month:
          return 50;
        default:
          return 50;
      }
    })();
    return result;
  };
  const getTimeStep = (v: ViewMode): number => {
    const result: number = (() => {
      switch (v) {
        case ViewMode.Hour:
          return 30 * 60 * 1000;
        case ViewMode.Day:
          return 12 * 60 * 60 * 1000;
        case ViewMode.Month:
          return 24 * 60 * 60 * 1000;
        default:
          return 0;
      }
    })();
    return result;
  };
  const handleTask = useCallback((task: CustomTask) => {
    detailApi.show(task.workId, task.planId);
  }, []);

  const onTaskClick = useCallback(() => {
    // スケジュール押下時
  }, []);

  if (status === ScheduleStatus.show) {
    return (
      <div>
        {detailContext}
        <div className="section schedule_overflow">
          <div className={styles.scheduletools}>
            <div>
              <Switch checked={isWorkChecked} onClick={() => setIsWorkChecked(!isWorkChecked)} />
              &nbsp;作業内容
            </div>
            <div className={styles.scheduleMetaInfo}>
              <div>検索結果：{scheduleList.length}件</div>
              <div>最終更新日時（検索時刻）：{formatDate(dayjs(), DateFormat.YYYYMMDDHHMMSS)}</div>
            </div>
          </div>
          <Gantt<CustomTask>
            tasks={showTasks}
            columnList={columnList(handleTask, isWorkChecked, isConflictListMark)}
            TooltipContent={NullToolTip}
            // timeStep={getTinhmeStep(view)}
            fixedDateOption={{
              start: viewDate.startOf('day').add(0, 'hour').toDate(),
              end: viewDate.endOf('day').toDate(),
            }}
            viewMode={view}
            viewDate={dayjs().subtract(1, 'day').endOf('day').toDate()} // 現在日0:00の1時間前を表示するが、実データが優先される
            locale="ja-JP"
            listCellWidth={isChecked ? '155px' : ''}
            columnWidth={getColumnWidth(view)}
            rowHeight={30}
            onClick={onTaskClick}
          />

        </div>
      </div>
    );
  }
  if (status === ScheduleStatus.loading) {
    return (
      <Skeleton.Button active={true} size='large' style={{ display: 'block', height: '100px' }} block={true} />
    );
  }
  if (status === ScheduleStatus.error_no_data) {
    return (
      <div className="section">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }
  if (status === ScheduleStatus.error_other) {
    return (
      <div className="section">
        <Alert message={Messages.ERR_OTHER} type="error" showIcon/>
      </div>
    );
  }
  if (status === ScheduleStatus.error_timeout) {
    return (
        <div className="section">
          <Alert message={Messages.ERR_DIALOG_TIMEOUT} type="error" showIcon/>
        </div>
    );
  }
  return (
    <></>
  );
};
